/**
 * Human-friendly relative time ("5 minutes ago", "2 days ago", "now") built on
 * the platform's existing Intl idiom (mirrors `format-currency.ts`) — no extra
 * date dependency. The formatter is module-cached.
 */
const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

const DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'seconds' },
  { amount: 60, unit: 'minutes' },
  { amount: 24, unit: 'hours' },
  { amount: 7, unit: 'days' },
  { amount: 4.34524, unit: 'weeks' },
  { amount: 12, unit: 'months' },
  { amount: Number.POSITIVE_INFINITY, unit: 'years' },
];

/**
 * @param iso ISO 8601 timestamp (e.g. a notification's `createdAt`).
 * @param now reference point; injectable for deterministic tests.
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  let duration = (new Date(iso).getTime() - now.getTime()) / 1000; // seconds; negative = past
  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return rtf.format(Math.round(duration), 'years');
}

const SEVEN_DAYS_MS = 7 * 86_400_000;
const absFmt = new Intl.DateTimeFormat('en', { day: 'numeric', month: 'long' });

/**
 * Drawer-row timestamp rule (ARC-58 / Figma): compact-relative for recent rows
 * — "Now" under a minute, then "20h ago" style for anything in the last week —
 * and an absolute date ("14 April") once older than 7 days.
 */
export function formatNotificationTime(iso: string, now: Date = new Date()): string {
  const diffMs = now.getTime() - new Date(iso).getTime();
  if (diffMs < 60_000) return 'Now';
  if (diffMs < SEVEN_DAYS_MS) return compactRelative(diffMs);
  return absFmt.format(new Date(iso));
}

/** "5m ago" / "3h ago" / "2d ago" — terse, for the last-week window. */
function compactRelative(diffMs: number): string {
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
